/**
 * Where a mutation's server failure is presented (#1174, owner Q-291 a).
 *
 * The answer goes where the request came from. `useMutation` from
 * `@/api/mutation` (and `useApiMutation` / `useBulkMutation`, which use it)
 * stamps `meta.feedbackLayer` with the dialog layer the calling component sits
 * in, captured at render. `routeMutationError` (the MutationCache `onError`)
 * reads it when a 5xx or transport failure settles:
 *
 *   - `{ layer, opening }` with a layer → `layer.push(copy, 'error', opening)`;
 *     the layer shows it in its band while that opening of the dialog is open
 *     and mounted, and forwards it to the global banner otherwise.
 *   - `{ layer: null }`, or no `feedbackLayer` at all → the global banner.
 *   - `CALLER_REPORTS` → nothing: the component awaiting the mutation presents
 *     the failure itself (the caller-mode hooks, #1174-d).
 *
 * Deliberately tiny and React-free: `serverErrorRouting.ts` imports it, and
 * must not pull React or dialog code into its import graph.
 */
import type { FeedbackPushOptions, FeedbackSeverity } from '@/store/useFeedbackStore';

/** The routing target a dialog layer exposes: its `push`. */
export interface FeedbackLayerSink {
  /**
   * Show `message` in the layer's band. `opening` is the opening of the
   * dialog the action ran in; a push for any other opening, or while the
   * dialog is closed or unmounted, goes to the global banner instead
   * (`error`/`warning`) or is dropped (`success`/`info`). The third argument
   * may instead be the global store's `FeedbackPushOptions` (Q-530 R8), which a
   * layer accepts and ignores (an options object means "this opening") and the
   * global banner applies.
   */
  push: (message: string, severity: FeedbackSeverity, openingOrOptions?: number | FeedbackPushOptions) => string;
  /**
   * The action `source` succeeded (#1628): remove it from every item here that
   * carries it. Items pushed without a source, or also by another source, stay.
   */
  clearSource: (source: string) => void;
}

/** The dialog layer a mutation's hook was called in, and that dialog's opening. */
export interface FeedbackLayerCapture {
  /** Null when the hook was called outside any dialog. */
  layer: FeedbackLayerSink | null;
  opening: number;
  /** The action's key (#1628): what central routing pushes with, and success clears. */
  source?: string;
  /**
   * Set when the hook has `feedbackKeyOf` (#1628-d4): the key of ONE run, from its variables.
   * Central routing pushes with `sourceOf(variables)` instead of `source`.
   */
  sourceOf?: (variables: never) => string;
}

/** The caller presents the failure; central routing pushes nothing. */
export const CALLER_REPORTS: unique symbol = Symbol('feedbackLayer.callerReports');

export type FeedbackLayerMeta = FeedbackLayerCapture | typeof CALLER_REPORTS;

/**
 * The app's mutation `meta`. A type alias, not an interface: TanStack only
 * accepts a `mutationMeta` that extends `Record<string, unknown>`, which an
 * interface (no index signature) does not, and it then silently falls back to
 * `Record<string, unknown>`. `test_1174_mutation_meta_type` pins it.
 */
export type AppMutationMeta = Record<string, unknown> & {
  feedbackLayer?: FeedbackLayerMeta;
};

declare module '@tanstack/react-query' {
  interface Register {
    mutationMeta: AppMutationMeta;
  }
}
